import { create } from 'zustand';

export type ConnectionType = 'usb' | 'bluetooth';
export type ConnectionStatus = 
  | 'idle' 
  | 'selecting' 
  | 'connecting' 
  | 'verifying' 
  | 'connected' 
  | 'failed';
export type ErrorType = 
  | 'no-device-found' 
  | 'connection-failed' 
  | 'health-check-failed' 
  | null;

export interface DeviceHealth {
  serialNumber: string;
  batteryLevel: number;
  temperature: number;
  firmwareVersion: string;
  isReady: boolean;
}

interface ConnectionWizardState {
  isOpen: boolean;
  connectionType: ConnectionType | null;
  connectionStatus: ConnectionStatus;
  // Widened to accept BluetoothDevice as groundwork for story 2's Connection Wizard BLE
  // wiring — no BLE call site sets this yet in this story.
  selectedDevice: USBDevice | BluetoothDevice | null;
  deviceHealth: DeviceHealth | null;
  errorType: ErrorType;
  errorMessage: string | null;
}

interface ConnectionWizardActions {
  open: () => void;
  close: () => void;
  selectConnectionType: (type: ConnectionType) => void;
  setConnectionStatus: (status: ConnectionStatus) => void;
  setSelectedDevice: (device: USBDevice | BluetoothDevice | null) => void;
  setDeviceHealth: (health: DeviceHealth | null) => void;
  setError: (type: ErrorType, message?: string) => void;
  clearError: () => void;
  reset: () => void;
}

type ConnectionWizardStore = ConnectionWizardState & ConnectionWizardActions;

const initialState: ConnectionWizardState = {
  isOpen: false,
  connectionType: null,
  connectionStatus: 'idle',
  selectedDevice: null,
  deviceHealth: null,
  errorType: null,
  errorMessage: null,
};

export const useConnectionWizard = create<ConnectionWizardStore>((set) => ({
  ...initialState,

  open: () => set({ isOpen: true, connectionStatus: 'selecting' }),
  
  close: () => set({ ...initialState, isOpen: false }),
  
  selectConnectionType: (type) => set({ connectionType: type }),
  
  setConnectionStatus: (status) => set({ connectionStatus: status }),
  
  setSelectedDevice: (device) => set({ selectedDevice: device }),
  
  setDeviceHealth: (health) => set({ deviceHealth: health }),
  
  setError: (type, message) => set({ 
    errorType: type, 
    errorMessage: message || null,
    connectionStatus: 'failed'
  }),
  
  clearError: () => set({ errorType: null, errorMessage: null }),
  
  reset: () => set(initialState),
}));
